import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import ReactDOM from 'react-dom'

import { ArcadeIcon } from '@arcade/web/icons/react'

import { useMediaQuery } from '../../hooks/useMediaQuery'
import { breakpoints, spacing } from '../../theme'
import { PersistentLiveRegion } from '../../utils/persistentLiveRegion'
import Buttonless from '../Buttonless/Buttonless'
import { Flex } from '../layout/Flex'
import { Position } from '../Position/Position'
import Text from '../Text/Text'

export const TRANSITION_DURATION = 200
export const DISMISS_TIMEOUT_WITH_BUTTON = 5000
export const DISMISS_TIMEOUT_WITHOUT_BUTTON = 3000
export const ANNOUNCE_DELAY = 1000 // Delay between announcements

// Queue for managing announcements
const announceQueue: (() => void)[] = []
let isAnnouncing = false

const processAnnounceQueue = () => {
  if (isAnnouncing || announceQueue.length === 0) return

  isAnnouncing = true
  const announce = announceQueue.shift()
  if (announce) {
    announce()
    setTimeout(() => {
      isAnnouncing = false
      processAnnounceQueue()
    }, ANNOUNCE_DELAY)
  }
}

export type ToastPosition = 'bottom-right' | 'bottom-center' | 'bottom-left' | 'top-right' | 'top-center' | 'top-left'

export interface ToastProps {
  headline: string
  icon?: ArcadeIcon
  body?: string
  buttonLabel?: string
  onClose?: () => void
  dismissTimeout?: number
  container?: HTMLElement
  bottomOffset?: string
  rightOffset?: string
  topOffset?: string
  leftOffset?: string
  width?: string
  position?: ToastPosition
}

interface TestIdProps {
  'data-testid'?: string
}

const ToastPortalContainer = styled(Position)<{
  $width?: string
  $isMobile?: boolean
  $position: ToastPosition
}>`
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: ${({ $width, $isMobile }) => ($width || $isMobile ? 'stretch' : 'flex-end')};
  z-index: 99999;
  box-sizing: border-box;
  width: ${({ $width, $isMobile }) => ($width ? $width : $isMobile ? '100%' : 'auto')};
  max-width: ${({ theme, $width, $isMobile }) =>
    $width ? $width : $isMobile ? '100%' : `calc(100% - ${theme.spacing.s} * 2)`};
  padding: ${({ theme, $width, $isMobile }) => ($width || $isMobile ? `0 ${theme.spacing.l}` : '0')};
`

const ToastContainer = styled(Flex)<{
  isMobile: boolean
  $opacity: number
  $transform: string
  $transition: string
  $pointerEvents: string
}>`
  display: ${({ isMobile }) => (isMobile ? 'flex' : 'inline-flex')};
  width: ${({ isMobile }) => (isMobile ? '100%' : 'auto')};
  min-width: ${({ isMobile, theme }) => (isMobile ? '100%' : theme.pxToRem(349))};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.xs} ${theme.spacing.xs} ${theme.spacing.m}`};
  border-radius: ${({ theme }) => theme.border.radius.small};
  background: ${({ theme }) => theme.arcadeColors.component.toastBackgroundDefault};
  color: ${({ theme }) => theme.arcadeColors.semantic.textInverse};
  opacity: ${({ $opacity }) => $opacity};
  transform: ${({ $transform }) => $transform};
  transition: ${({ $transition }) => $transition};
  pointer-events: ${({ $pointerEvents }) => $pointerEvents};
`

const TextContainer = styled(Flex)`
  flex: 1;
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.xs} ${theme.spacing.xs} 0`};
`

const StyledActionButton = styled(Buttonless)`
  width: auto;
  min-height: 0;
  height: auto;
  color: inherit;
  border-radius: ${({ theme }) => theme.border.radius.xsmall};
  padding: ${({ theme }) => theme.spacing.xs};
  ${({ theme }) => theme.typography.buttonCompact};

  &:active {
    background: ${({ theme }) => theme.arcadeColors.component.toastButtonBackgroundPressed};
  }

  ${({ theme }) => theme.selectors.keyboard} &:focus {
    border-radius: inherit;
    outline: ${({ theme }) => theme.border.width.small} solid
      ${({ theme }) => theme.arcadeColors.semantic.borderInverse};
  }
`

const IconWrapper = styled.div`
  height: ${({ theme }) => theme.pxToRem(24)};
  width: ${({ theme }) => theme.pxToRem(24)};
`

const getIcon = (Icon: ArcadeIcon) => <Icon size={24} />

interface PositionStylesParams {
  position: ToastPosition
  topOffset?: string
  leftOffset?: string
  bottomOffset?: string
  rightOffset?: string
  isMobile: boolean
}

const getPositionStyles = ({
  position,
  topOffset,
  leftOffset,
  bottomOffset,
  rightOffset,
  isMobile,
}: PositionStylesParams) => {
  const defaultXOffset = isMobile ? '0' : spacing.s
  const defaultYOffset = spacing.s

  const styles: {
    position: 'fixed'
    top?: string
    bottom?: string
    left?: string
    right?: string
    transform?: string
  } = {
    position: 'fixed',
  }

  switch (position) {
    case 'bottom-right':
      styles.right = rightOffset || defaultXOffset
      styles.bottom = bottomOffset || defaultYOffset
      styles.top = 'auto'
      styles.left = 'auto'
      styles.transform = 'none'
      break
    case 'bottom-center':
      styles.bottom = bottomOffset || defaultYOffset
      styles.left = '50%'
      styles.transform = 'translateX(-50%)'
      styles.right = 'auto'
      styles.top = 'auto'
      break
    case 'bottom-left':
      styles.left = leftOffset || defaultXOffset
      styles.bottom = bottomOffset || defaultYOffset
      styles.right = 'auto'
      styles.top = 'auto'
      styles.transform = 'none'
      break
    case 'top-right':
      styles.right = rightOffset || defaultXOffset
      styles.top = topOffset || defaultYOffset
      styles.bottom = 'auto'
      styles.left = 'auto'
      styles.transform = 'none'
      break
    case 'top-center':
      styles.top = topOffset || defaultYOffset
      styles.left = '50%'
      styles.transform = 'translateX(-50%)'
      styles.right = 'auto'
      styles.bottom = 'auto'
      break
    case 'top-left':
      styles.left = leftOffset || defaultXOffset
      styles.top = topOffset || defaultYOffset
      styles.right = 'auto'
      styles.bottom = 'auto'
      styles.transform = 'none'
      break
  }

  return styles
}

const Toast = forwardRef<HTMLDivElement, ToastProps & TestIdProps>((props, ref) => {
  const { spacing } = useTheme()
  const {
    headline,
    icon,
    body,
    buttonLabel,
    onClose,
    'data-testid': testId,
    dismissTimeout,
    container,
    bottomOffset,
    rightOffset,
    leftOffset,
    topOffset,
    width,
    position = 'bottom-right',
  } = props
  const isMobile = !useMediaQuery(breakpoints.tablet)

  const [isVisible, setIsVisible] = useState(true)
  const [isClosing, setIsClosing] = useState(false)
  const [hasEntered, setHasEntered] = useState(false)
  const defaultDismissTimeout = buttonLabel ? DISMISS_TIMEOUT_WITH_BUTTON : DISMISS_TIMEOUT_WITHOUT_BUTTON
  const effectiveDismissTimeout = dismissTimeout ?? defaultDismissTimeout
  const actionButtonRef = useRef<HTMLButtonElement>(null)
  const lastFocusedElement = useRef<HTMLElement | null>(null)

  // Store the last focused element when toast becomes visible
  useEffect(() => {
    if (isVisible && !isClosing) {
      lastFocusedElement.current = document.activeElement as HTMLElement
    }
  }, [isVisible, isClosing])

  // Trigger enter animation after mount and queue announcement
  useEffect(() => {
    const message = body ? `${headline} ${body}` : headline
    announceQueue.push(() => {
      PersistentLiveRegion.announce(message, { politeness: 'polite', duration: 3000 })
    })
    processAnnounceQueue()
    setHasEntered(true)
  }, [body, headline])

  // Only focus the action button if present
  useEffect(() => {
    if (isVisible && !isClosing && buttonLabel && actionButtonRef.current) {
      actionButtonRef.current?.focus()
    }
  }, [isVisible, isClosing, buttonLabel])

  const handleClose = useCallback(() => {
    setIsClosing(true)
    setTimeout(() => {
      setIsVisible(false)
      onClose?.()
      // Restore focus after toast is removed
      if (lastFocusedElement.current) {
        lastFocusedElement.current.focus()
      }
    }, TRANSITION_DURATION)
  }, [onClose])

  useEffect(() => {
    if (!isVisible || isClosing) return
    const timer = setTimeout(() => {
      handleClose()
    }, effectiveDismissTimeout)
    return () => clearTimeout(timer)
  }, [effectiveDismissTimeout, handleClose, isVisible, isClosing])

  // Animation logic
  let opacity = 1
  let transform = 'translateY(0) scale(1)'
  const transition = `opacity ${TRANSITION_DURATION}ms, transform ${TRANSITION_DURATION}ms`
  let transitionTimingFunction = 'ease-out'

  if (!hasEntered && !isClosing) {
    // Initial render, before enter effect
    opacity = 0
    transform = 'translateY(8px) scale(1)'
  } else if (isClosing) {
    // Closing animation
    opacity = 0
    transform = 'translateY(0) scale(0.9)'
    transitionTimingFunction = 'ease-in'
  }

  const positionStyles = getPositionStyles({
    position,
    topOffset,
    leftOffset,
    bottomOffset,
    rightOffset,
    isMobile,
  })

  return ReactDOM.createPortal(
    <ToastPortalContainer
      position={positionStyles.position}
      top={positionStyles.top}
      bottom={positionStyles.bottom}
      left={positionStyles.left}
      right={positionStyles.right}
      style={{ transform: positionStyles.transform }}
      $width={width}
      $isMobile={isMobile}
      $position={position}
      data-testid={testId}
    >
      <ToastContainer
        role="status"
        alignItems="center"
        gap={spacing.s}
        isMobile={isMobile}
        $opacity={opacity}
        $transform={transform}
        $transition={`${transition} ${transitionTimingFunction}`}
        $pointerEvents={isClosing ? 'none' : 'auto'}
      >
        {icon && <IconWrapper data-testid="toast-icon">{getIcon(icon)}</IconWrapper>}
        <TextContainer flexDirection="column" gap={spacing.empty}>
          <Text typography="labelMedium">{headline}</Text>
          {body && <Text typography="body">{body}</Text>}
        </TextContainer>
        {buttonLabel && (
          <StyledActionButton type="button" onClick={handleClose} aria-label={buttonLabel} ref={actionButtonRef}>
            {buttonLabel}
          </StyledActionButton>
        )}
      </ToastContainer>
    </ToastPortalContainer>,
    container || document.body
  )
})

export default Toast
